import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type {
  Call,
  CallScorecardCriterion,
  CallScorecardEntry,
} from "@sales-ops-amplify/shared";
import { saveCallScorecard } from "@/api/calls";

type Props = {
  call: Call;
  criteria: CallScorecardCriterion[];
  onCallUpdated?: (call: Call) => void;
};

export function CallScorecardEditor({ call, criteria, onCallUpdated }: Props) {
  const [entries, setEntries] = useState<CallScorecardEntry[]>([]);

  useEffect(() => {
    const existing = new Map(
      (call.scorecard ?? []).map((e) => [e.criterionId, e]),
    );
    setEntries(
      criteria.map((c) => {
        const prev = existing.get(c.id);
        return {
          criterionId: c.id,
          label: c.label,
          score: prev?.score ?? 0,
          maxScore: c.maxScore,
          note: prev?.note,
        };
      }),
    );
  }, [call.callId, call.scorecard, criteria]);

  const saveMut = useMutation({
    mutationFn: () => saveCallScorecard(call.callId, entries),
    onSuccess: (res) => onCallUpdated?.(res.call),
  });

  if (criteria.length === 0) return null;

  const total = entries.reduce((s, e) => s + e.score, 0);
  const max = entries.reduce((s, e) => s + e.maxScore, 0);

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase text-muted-foreground">
          Scorecard
        </p>
        <p className="text-xs text-muted-foreground">
          {total}/{max}
        </p>
      </div>
      <div className="space-y-2">
        {entries.map((e, idx) => (
          <label key={e.criterionId} className="block text-xs">
            <span className="flex justify-between gap-2">
              <span>{e.label}</span>
              <span className="text-muted-foreground">
                {e.score}/{e.maxScore}
              </span>
            </span>
            <input
              type="range"
              min={0}
              max={e.maxScore}
              step={1}
              className="mt-1 w-full"
              value={e.score}
              onChange={(ev) => {
                const score = Number(ev.target.value);
                setEntries((prev) =>
                  prev.map((row, i) => (i === idx ? { ...row, score } : row)),
                );
              }}
            />
          </label>
        ))}
      </div>
      <button
        type="button"
        className="rounded-md border border-border px-2 py-1 text-xs"
        disabled={saveMut.isPending}
        onClick={() => saveMut.mutate()}
      >
        {saveMut.isPending ? "Saving…" : "Save scorecard"}
      </button>
    </div>
  );
}
